/**
 * LCForm Component
 *
 * Dialog form for creating and editing liquid cultures.
 * Supports strain selection, generation tracking, and parent culture linking.
 */

import { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useParentCultures } from '@/hooks/useLC'
import { useLCMutations } from '@/hooks/useLCMutations'
import { useAvailableSyringes } from '@/hooks/useCultureSyringes'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { AlertTriangle, Beaker, Calendar, Droplet, Syringe } from 'lucide-react'
import { toLocalDateString } from '@/utils/dateUtils'

// Volume options in ml
const volumeOptions = [100, 250, 500, 1000, 2000]

/**
 * Calculate ready date (14 days from inoculation)
 * @param {string} inoculationDate - ISO date string
 * @returns {string} ISO date string for ready date
 */
function calculateReadyDate(inoculationDate) {
  if (!inoculationDate) return ''
  const date = new Date(inoculationDate)
  date.setDate(date.getDate() + 14)
  return toLocalDateString(date)
}


export default function LCForm({
  isOpen,
  onClose,
  onSave,
  editingLC = null
}) {
  const { createLC, updateLC, loading: mutationLoading } = useLCMutations()

  // Source type: 'parent' for in-house LC, 'syringe' for third-party
  const [sourceType, setSourceType] = useState('parent')

  const [formData, setFormData] = useState({
    generation: 1,
    volume_ml: 500,
    inoculation_date: toLocalDateString(new Date()),
    parent_culture_id: '',
    source_syringe_id: '',
    volume_used_ml: '',
    storage_location: '',
    notes: ''
  })

  const [errors, setErrors] = useState({})

  // Fetch all parent cultures with strain data (for grouped dropdown)
  const { parentCultures, parentCulturesByStrain, loading: parentsLoading } = useParentCultures()

  // Fetch all available syringes with strain data (for grouped dropdown)
  const { syringes: availableSyringes, syringesByStrain, isLoading: syringesLoading } = useAvailableSyringes()

  // Calculate ready date based on inoculation date
  const readyDate = useMemo(
    () => calculateReadyDate(formData.inoculation_date),
    [formData.inoculation_date]
  )


  // Determine suggested generation based on parent
  const suggestedGeneration = useMemo(() => {
    if (!formData.parent_culture_id || !parentCultures.length) return 1
    const parent = parentCultures.find(p => p.id === formData.parent_culture_id)
    if (!parent) return 1
    return Math.min(parent.generation + 1, 3)
  }, [formData.parent_culture_id, parentCultures])

  // Get selected parent culture details
  const selectedParent = useMemo(() => {
    if (!formData.parent_culture_id || !parentCultures.length) return null
    return parentCultures.find(p => p.id === formData.parent_culture_id)
  }, [formData.parent_culture_id, parentCultures])

  // Get selected syringe details
  const selectedSyringe = useMemo(() => {
    if (!formData.source_syringe_id || !availableSyringes?.length) return null
    return availableSyringes.find(s => s.id === formData.source_syringe_id)
  }, [formData.source_syringe_id, availableSyringes])

  // Derive strain from selected parent or syringe
  const derivedStrain = useMemo(() => {
    if (sourceType === 'parent' && selectedParent?.strain) {
      return selectedParent.strain
    }
    if (sourceType === 'syringe' && selectedSyringe?.strain) {
      return selectedSyringe.strain
    }
    return null
  }, [sourceType, selectedParent, selectedSyringe])

  // Initialize form when opening
  useEffect(() => {
    if (isOpen) {
      if (editingLC) {
        // Editing mode - determine source type from existing LC
        const hasSyringeSource = !!editingLC.source_syringe_id
        setSourceType(hasSyringeSource ? 'syringe' : 'parent')
        setFormData({
          generation: editingLC.generation || 1,
          volume_ml: editingLC.volume_ml || 500,
          inoculation_date: editingLC.inoculation_date || toLocalDateString(new Date()),
          parent_culture_id: editingLC.parent_culture_id || '',
          source_syringe_id: editingLC.source_syringe_id || '',
          volume_used_ml: '',
          storage_location: editingLC.storage_location || '',
          notes: editingLC.notes || ''
        })
      } else {
        // New LC - reset everything
        setSourceType('parent')
        setFormData({
          generation: 1,
          volume_ml: 500,
          inoculation_date: toLocalDateString(new Date()),
          parent_culture_id: '',
          source_syringe_id: '',
          volume_used_ml: '',
          storage_location: '',
          notes: ''
        })
      }
      setErrors({})
    }
  }, [isOpen, editingLC])

  // Auto-update generation when parent changes
  useEffect(() => {
    if (!editingLC && formData.parent_culture_id && sourceType === 'parent') {
      setFormData(prev => ({
        ...prev,
        generation: suggestedGeneration
      }))
    }
  }, [formData.parent_culture_id, suggestedGeneration, editingLC, sourceType])

  // Handle source type changes
  useEffect(() => {
    if (!editingLC) {
      if (sourceType === 'syringe') {
        // Syringe source: always G1, clear parent
        setFormData(prev => ({
          ...prev,
          generation: 1,
          parent_culture_id: ''
        }))
      } else {
        // Parent source: clear syringe fields
        setFormData(prev => ({
          ...prev,
          source_syringe_id: '',
          volume_used_ml: ''
        }))
      }
    }
  }, [sourceType, editingLC])

  const validateForm = () => {
    const newErrors = {}

    if (!formData.volume_ml || formData.volume_ml <= 0) {
      newErrors.volume_ml = 'Volume must be greater than 0'
    }

    if (!formData.inoculation_date) {
      newErrors.inoculation_date = 'Inoculation date is required'
    }

    if (sourceType === 'syringe' && !editingLC) {
      // Syringe-specific validation
      if (!formData.source_syringe_id) {
        newErrors.source_syringe_id = 'Select a syringe'
      }
      const volumeUsed = parseFloat(formData.volume_used_ml)
      if (!volumeUsed || volumeUsed <= 0) {
        newErrors.volume_used_ml = 'Enter volume to use'
      } else if (selectedSyringe && volumeUsed > selectedSyringe.volume_remaining_ml) {
        newErrors.volume_used_ml = `Only ${selectedSyringe.volume_remaining_ml}ml available`
      }
    } else if (sourceType === 'parent' && !editingLC) {
      // Parent culture validation - require a parent to be selected
      if (!formData.parent_culture_id) {
        newErrors.parent_culture_id = 'Select a parent culture'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    try {
      let savedLC
      if (editingLC) {
        savedLC = await updateLC(editingLC.id, {
          volume_ml: formData.volume_ml,
          storage_location: formData.storage_location || null,
          notes: formData.notes || null
        })
      } else {
        // Build create payload based on source type
        const createPayload = {
          strain_id: derivedStrain?.id,
          generation: formData.generation,
          volume_ml: formData.volume_ml,
          inoculation_date: formData.inoculation_date,
          storage_location: formData.storage_location || null,
          notes: formData.notes || null
        }

        if (sourceType === 'syringe') {
          // Syringe source - include syringe data
          createPayload.source_syringe_id = formData.source_syringe_id
          createPayload.volume_used_ml = parseFloat(formData.volume_used_ml)
          createPayload.parent_culture_id = null
        } else {
          // Parent culture source
          createPayload.parent_culture_id = formData.parent_culture_id || null
          createPayload.source_syringe_id = null
        }

        savedLC = await createLC(createPayload)
      }

      if (onSave) {
        await onSave(savedLC)
      }
      onClose()
    } catch (error) {
      // Error handling is done in the mutation hook
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const isLoading = mutationLoading || parentsLoading || syringesLoading

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[596px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Beaker className="h-5 w-5" aria-hidden="true" />
            {editingLC ? 'Edit Liquid Culture' : 'New Liquid Culture'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Source Type Selection - only for new LCs */}
          {!editingLC && (
            <div className="space-y-1.5">
              <Label>Inoculation Source</Label>
              <RadioGroup
                value={sourceType}
                onValueChange={setSourceType}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="parent" id="source-parent" />
                  <Label htmlFor="source-parent" className="font-normal cursor-pointer">
                    From Parent Culture
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="syringe" id="source-syringe" />
                  <Label htmlFor="source-syringe" className="font-normal cursor-pointer flex items-center gap-1">
                    <Syringe className="h-4 w-4" />
                    From Syringe
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {/* Syringe Source Fields */}
          {sourceType === 'syringe' && !editingLC && (
            <div className="space-y-3 p-3 bg-muted/30 rounded-lg border">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="syringe">
                    Select Syringe <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.source_syringe_id || 'none'}
                    onValueChange={(value) => handleInputChange('source_syringe_id', value === 'none' ? '' : value)}
                    disabled={syringesLoading}
                  >
                    <SelectTrigger
                      id="syringe"
                      className={errors.source_syringe_id ? 'border-red-500' : ''}
                    >
                      <SelectValue placeholder={
                        syringesLoading
                          ? "Loading..."
                          : availableSyringes?.length
                            ? "Select syringe"
                            : "No syringes available"
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(syringesByStrain).map(([strainName, strainSyringes]) => (
                        <SelectGroup key={strainName}>
                          <SelectLabel className="font-semibold text-foreground">{strainName}</SelectLabel>
                          {strainSyringes.map(syringe => (
                            <SelectItem key={syringe.id} value={syringe.id}>
                              <span className="font-mono">{syringe.supplier_batch_code}</span>
                              <span className="text-muted-foreground ml-2">
                                ({syringe.volume_remaining_ml}ml left)
                              </span>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.source_syringe_id && (
                    <p className="text-red-500 text-sm">{errors.source_syringe_id}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label className="text-muted-foreground">Strain (derived)</Label>
                  <div className="h-9 px-3 flex items-center rounded-md border bg-muted/50 text-sm">
                    {derivedStrain?.name || <span className="text-muted-foreground">Select syringe</span>}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="volume_used">
                    Use (ml) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="volume_used"
                    type="number"
                    step="0.5"
                    min="0.5"
                    max={selectedSyringe?.volume_remaining_ml || 100}
                    value={formData.volume_used_ml}
                    onChange={(e) => handleInputChange('volume_used_ml', e.target.value)}
                    placeholder="1.0"
                    className={errors.volume_used_ml ? 'border-red-500' : ''}
                  />
                  {errors.volume_used_ml && (
                    <p className="text-red-500 text-sm">{errors.volume_used_ml}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="volume_syringe" className="flex items-center gap-1">
                    <Droplet className="h-3.5 w-3.5" aria-hidden="true" />
                    LC Volume <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.volume_ml.toString()}
                    onValueChange={(value) => handleInputChange('volume_ml', parseInt(value))}
                  >
                    <SelectTrigger
                      id="volume_syringe"
                      className={errors.volume_ml ? 'border-red-500' : ''}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {volumeOptions.map(vol => (
                        <SelectItem key={vol} value={vol.toString()}>
                          {vol} ml
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-muted-foreground">Generation</Label>
                  <div className="h-9 flex items-center px-3 rounded-md border bg-muted/50 text-sm text-muted-foreground">
                    G1 (external)
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Parent Culture Source - only for parent source */}
          {sourceType === 'parent' && !editingLC && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="parent">
                  Parent Culture <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.parent_culture_id || 'none'}
                  onValueChange={(value) => handleInputChange('parent_culture_id', value === 'none' ? '' : value)}
                  disabled={parentsLoading}
                >
                  <SelectTrigger
                    id="parent"
                    className={errors.parent_culture_id ? 'border-red-500' : ''}
                  >
                    <SelectValue placeholder={
                      parentsLoading
                        ? "Loading..."
                        : parentCultures.length
                          ? "Select parent culture"
                          : "No parents available"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(parentCulturesByStrain).map(([strainName, strainParents]) => (
                      <SelectGroup key={strainName}>
                        <SelectLabel className="font-semibold text-foreground">{strainName}</SelectLabel>
                        {strainParents.map(parent => (
                          <SelectItem key={parent.id} value={parent.id}>
                            <span className="font-mono">{parent.culture_code}</span>
                            <span className="text-muted-foreground ml-2">
                              (G{parent.generation}, {parent.status})
                            </span>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
                {errors.parent_culture_id && (
                  <p className="text-red-500 text-sm">{errors.parent_culture_id}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-muted-foreground">Strain (derived)</Label>
                <div className="h-9 px-3 flex items-center rounded-md border bg-muted/50 text-sm">
                  {derivedStrain?.name || <span className="text-muted-foreground">Select parent</span>}
                </div>
              </div>
            </div>
          )}

          {/* Volume, Generation - only for parent source */}
          {sourceType === 'parent' && (
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="volume" className="flex items-center gap-1">
                  <Droplet className="h-3.5 w-3.5" aria-hidden="true" />
                  Volume <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.volume_ml.toString()}
                  onValueChange={(value) => handleInputChange('volume_ml', parseInt(value))}
                >
                  <SelectTrigger
                    id="volume"
                    className={errors.volume_ml ? 'border-red-500' : ''}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {volumeOptions.map(vol => (
                      <SelectItem key={vol} value={vol.toString()}>
                        {vol} ml
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.volume_ml && (
                  <p className="text-red-500 text-sm">{errors.volume_ml}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="generation">Generation</Label>
                <Select
                  value={formData.generation.toString()}
                  onValueChange={(value) => handleInputChange('generation', parseInt(value))}
                  disabled={!!editingLC}
                >
                  <SelectTrigger id="generation">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">G1 (Master)</SelectItem>
                    <SelectItem value="2">G2 (Transfer)</SelectItem>
                    <SelectItem value="3">G3 (Final)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-muted-foreground">From Parent</Label>
                <div className="h-9 px-3 flex items-center rounded-md border bg-muted/50 text-sm text-muted-foreground truncate">
                  {selectedParent ? `G${selectedParent.generation} → G${formData.generation}` : 'New master'}
                </div>
              </div>
            </div>
          )}

          {/* Generation Warning - only for parent source */}
          {sourceType === 'parent' && formData.generation === 3 && (
            <Alert className="border-orange-200 bg-orange-50">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-700">
                G3 cultures are the final generation. Consider creating a fresh G1 master
                culture to maintain strain vitality.
              </AlertDescription>
            </Alert>
          )}

          {/* Date Row: Inoculation Date + Lifetime + Ready Date */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="inoculation_date" className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
                Inoculation <span className="text-red-500">*</span>
              </Label>
              <Input
                id="inoculation_date"
                type="date"
                value={formData.inoculation_date}
                onChange={(e) => handleInputChange('inoculation_date', e.target.value)}
                disabled={!!editingLC}
                className={errors.inoculation_date ? 'border-red-500' : ''}
              />
              {errors.inoculation_date && (
                <p className="text-red-500 text-sm">{errors.inoculation_date}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-muted-foreground">Lifetime</Label>
              <div className="h-9 px-3 flex items-center rounded-md border bg-muted/50 text-sm text-muted-foreground">
                14 days
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-muted-foreground">Ready Date</Label>
              <div className="h-9 px-3 flex items-center rounded-md border bg-muted/50 text-sm">
                {readyDate ? new Date(readyDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                }) : '-'}
              </div>
            </div>
          </div>

          {/* Storage Location */}
          <div className="space-y-1.5">
            <Label htmlFor="storage_location">Storage Location</Label>
            <Input
              id="storage_location"
              value={formData.storage_location}
              onChange={(e) => handleInputChange('storage_location', e.target.value)}
              placeholder="e.g., Lab Fridge A, Shelf 2"
            />
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Optional notes..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant={editingLC ? 'default' : 'create'}
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : editingLC ? 'Update' : 'Create LC'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
